import { c as _c } from "react/compiler-runtime";
// ИИ-загрузка каталога: список заданий и запуск нового (/catalog-import).
// Адрес `?applicationId=` открывает «Новую загрузку» с новым магазином из
// заявки, `?shopId=` — с существующим магазином (кнопки на карточках
// магазина, продавца и заявки).
import { Plus, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { CATALOG_IMPORT_JOB_STATUSES, CATALOG_IMPORT_JOB_STATUS_LABELS, type CatalogImportJob, type CatalogImportJobStatus } from "@osmix/contracts";
import { useCatalogImportJobs } from "@/api/hooks/use-catalog-import";
import { NewImportDialog, type NewImportPreset } from "@/components/catalog-import/new-import-dialog";
import { JobStatusPill, TemplateDownload, formatDate, formatUsd, targetText } from "@/components/catalog-import/shared";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips } from "@/components/filter-chips";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useDataTable } from "@/lib/use-data-table";
import { useAuth } from "@/store";
type SortKey = "title" | "items" | "cost" | "created";
export function CatalogImportPage() {
  const $ = _c(72);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const t0 = useAuth(_temp);
  let t1;
  if ($[0] !== t0) {
    t1 = hasAdminPermission(t0, "action.catalog_import.run");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canRun = t1;
  const jobsQuery = useCatalogImportJobs();
  const jobs = jobsQuery.data ?? [];
  let t2;
  if ($[2] !== params) {
    t2 = params.get("applicationId");
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const applicationId = t2;
  let t3;
  if ($[4] !== params) {
    t3 = params.get("shopId");
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const shopId = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      open: false,
      preset: null
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [dialog, setDialog] = useState(t4);
  let t5;
  if ($[7] !== applicationId || $[8] !== canRun || $[9] !== setDialog || $[10] !== shopId) {
    t5 = () => {
      if (!canRun) {
        return;
      }
      if (applicationId) {
        setDialog({
          open: true,
          preset: {
            kind: "new_shop",
            applicationId
          }
        });
      } else {
        if (shopId) {
          setDialog({
            open: true,
            preset: {
              kind: "existing_shop",
              shopId
            }
          });
        }
      }
    };
    $[7] = applicationId;
    $[8] = canRun;
    $[9] = setDialog;
    $[10] = shopId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== applicationId || $[13] !== canRun || $[14] !== shopId) {
    t6 = [applicationId, shopId, canRun];
    $[12] = applicationId;
    $[13] = canRun;
    $[14] = shopId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== applicationId || $[17] !== params || $[18] !== setDialog || $[19] !== setParams || $[20] !== shopId) {
    t7 = open => {
      if (open) {
        return;
      }
      setDialog({
        open: false,
        preset: null
      });
      if (applicationId || shopId) {
        const next = new URLSearchParams(params);
        next.delete("applicationId");
        next.delete("shopId");
        setParams(next, {
          replace: true
        });
      }
    };
    $[16] = applicationId;
    $[17] = params;
    $[18] = setDialog;
    $[19] = setParams;
    $[20] = shopId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const closeDialog = t7;
  let t8;
  if ($[22] !== params) {
    const statusParam = params.get("status") ?? "";
    t8 = (CATALOG_IMPORT_JOB_STATUSES as readonly string[]).includes(statusParam) ? statusParam as CatalogImportJobStatus : "";
    $[22] = params;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const status = t8;
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const filtered = jobs.filter(job => (!status || job.status === status) && (!q || [job.title, targetText(job), job.createdBy?.name ?? ""].join(" ").toLowerCase().includes(q)));
  let t10;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      key: "created",
      dir: "desc"
    };
    t10 = {
      title: _temp2,
      items: _temp3,
      cost: _temp4,
      created: _temp5
    };
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  let t11;
  if ($[26] !== filtered) {
    t11 = {
      rows: filtered,
      pageSize: 25,
      initialSort: t9,
      accessors: t10
    };
    $[26] = filtered;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const table = useDataTable(t11);
  let t12;
  if ($[28] !== params || $[29] !== setParams) {
    t12 = value => {
      const next_0 = new URLSearchParams(params);
      if (value) {
        next_0.set("status", value);
      } else {
        next_0.delete("status");
      }
      setParams(next_0, {
        replace: true
      });
    };
    $[28] = params;
    $[29] = setParams;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const setStatus = t12;
  let t13;
  if ($[31] !== navigate) {
    t13 = job_4 => void navigate(`/catalog-import/${job_4.id}`);
    $[31] = navigate;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const open_0 = t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="min-w-0 max-w-2xl"><h1 className="text-2xl font-bold tracking-tight">Загрузка каталога</h1><p className="text-sm text-muted-foreground">Закиньте прайс, каталог PDF, папку с фото или текст из переписки — модель разложит их в магазин и товары. Перед загрузкой всё проверяете вы.</p></div>;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TemplateDownload />;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== canRun || $[36] !== setDialog) {
    t16 = canRun && <Button onClick={() => setDialog({
      open: true,
      preset: null
    })}><Plus className="size-4" />Новая загрузка</Button>;
    $[35] = canRun;
    $[36] = setDialog;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <div className="flex flex-wrap items-start justify-between gap-4">{t14}<div className="flex flex-wrap items-center gap-2">{t15}{t16}</div></div>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = jobs.length;
  let t19;
  if ($[40] !== jobs) {
    t19 = CATALOG_IMPORT_JOB_STATUSES.map(s_0 => ({
      value: s_0,
      label: CATALOG_IMPORT_JOB_STATUS_LABELS[s_0],
      count: jobs.filter(job_5 => job_5.status === s_0).length,
      tone: s_0 === "failed" ? "danger" : s_0 === "review" ? "warning" : "default"
    }));
    $[40] = jobs;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== jobs.length || $[43] !== setStatus || $[44] !== status || $[45] !== t19) {
    t20 = <FilterChips value={status} onChange={setStatus} allLabel={"\u0412\u0441\u0435"} allCount={t18} chips={t19} />;
    $[42] = jobs.length;
    $[43] = setStatus;
    $[44] = status;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== setSearch) {
    t21 = e => setSearch(e.target.value);
    $[47] = setSearch;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== search || $[50] !== t21) {
    t22 = <Input value={search} onChange={t21} placeholder={"\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044E, \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0443, \u0430\u0432\u0442\u043E\u0440\u0443"} aria-label={"\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u0437\u0430\u0434\u0430\u043D\u0438\u044F\u043C"} className="h-9 w-full sm:max-w-xs" />;
    $[49] = search;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== jobsQuery.error) {
    t23 = jobsQuery.error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{jobsQuery.error.message}</div>;
    $[52] = jobsQuery.error;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== canRun || $[55] !== jobs.length || $[56] !== jobsQuery.isPending || $[57] !== open_0 || $[58] !== setDialog || $[59] !== table) {
    t24 = jobsQuery.isPending ? <p className="text-sm text-muted-foreground">Загружаем задания…</p> : jobs.length === 0 ? <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center"><WandSparkles className="size-8 text-muted-foreground" /><p className="max-w-md text-sm text-muted-foreground">Заданий пока нет. Начните с «Новой загрузки» — подойдёт любой прайс, каталог PDF или архив с фото.</p>{canRun && <Button onClick={() => setDialog({
        open: true,
        preset: null
      })}><Plus className="size-4" />Новая загрузка</Button>}</div> : <><ul className="flex flex-col gap-2 md:hidden">{table.pageRows.map(job_6 => <li key={job_6.id}><button type="button" onClick={() => open_0(job_6)} className="flex w-full flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"><span className="flex items-start justify-between gap-2"><span className="min-w-0 font-medium">{job_6.title}</span><JobStatusPill status={job_6.status} /></span><span className="truncate text-sm text-muted-foreground">{targetText(job_6)}</span><span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums"><span>позиций: {job_6.stats.items || "\u2014"}</span><span>загружено: {job_6.stats.committed || "\u2014"}</span>{job_6.usage.costUsd > 0 && <span>{formatUsd(job_6.usage.costUsd)}</span>}<span>{formatDate(job_6.createdAt)}</span></span></button></li>)}{table.total === 0 && <li className="p-4 text-center text-sm text-muted-foreground">Ничего не нашлось</li>}</ul><div className="hidden md:block"><Table><TableHeader><TableRow><SortableHead label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"} sortKey="title" sort={table.sort} onToggle={table.toggleSort} /><TableHead>Куда</TableHead><TableHead>Статус</TableHead><SortableHead label={"\u041F\u043E\u0437\u0438\u0446\u0438\u0439"} sortKey="items" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" /><TableHead className="text-right">Загружено</TableHead><SortableHead label={"\u041F\u043E\u0442\u0440\u0430\u0447\u0435\u043D\u043E"} sortKey="cost" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" /><TableHead className="hidden xl:table-cell">Автор</TableHead><SortableHead label={"\u0421\u043E\u0437\u0434\u0430\u043D\u043E"} sortKey="created" sort={table.sort} onToggle={table.toggleSort} /></TableRow></TableHeader><TableBody>{table.total === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Ничего не нашлось</TableCell></TableRow>}{table.pageRows.map(job_7 => <TableRow key={job_7.id} className="cursor-pointer" onClick={() => open_0(job_7)}><TableCell className="max-w-[260px] truncate font-medium" title={job_7.title}>{job_7.title}</TableCell><TableCell className="max-w-[220px] truncate text-muted-foreground" title={targetText(job_7)}>{targetText(job_7)}</TableCell><TableCell><JobStatusPill status={job_7.status} /></TableCell><TableCell className="text-right tabular-nums">{job_7.stats.items || "\u2014"}</TableCell><TableCell className="text-right tabular-nums">{job_7.stats.committed || job_7.stats.failed ? <>{job_7.stats.committed}{job_7.stats.failed > 0 && <span className="block text-xs whitespace-nowrap text-destructive">{job_7.stats.failed} с ошибкой</span>}</> : "\u2014"}</TableCell><TableCell className="text-right tabular-nums">{job_7.usage.costUsd ? formatUsd(job_7.usage.costUsd) : "\u2014"}</TableCell><TableCell className="hidden max-w-[160px] truncate text-muted-foreground xl:table-cell">{job_7.createdBy?.name ?? "\u2014"}</TableCell><TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">{formatDate(job_7.createdAt)}</TableCell></TableRow>)}</TableBody></Table></div>{table.total > 0 && <DataTablePagination table={table} />}</>;
    $[54] = canRun;
    $[55] = jobs.length;
    $[56] = jobsQuery.isPending;
    $[57] = open_0;
    $[58] = setDialog;
    $[59] = table;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== closeDialog || $[62] !== dialog.open || $[63] !== dialog.preset) {
    t25 = <NewImportDialog open={dialog.open} preset={dialog.preset} onOpenChange={closeDialog} />;
    $[61] = closeDialog;
    $[62] = dialog.open;
    $[63] = dialog.preset;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t17 || $[66] !== t20 || $[67] !== t22 || $[68] !== t23 || $[69] !== t24 || $[70] !== t25) {
    t26 = <div className="flex flex-col gap-4">{t17}{t20}{t22}{t23}{t24}{t25}</div>;
    $[65] = t17;
    $[66] = t20;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  return t26;
}
function _temp5(job_3) {
  return job_3.createdAt;
}
function _temp4(job_2) {
  return job_2.usage.costUsd;
}
function _temp3(job_1) {
  return job_1.stats.items;
}
function _temp2(job_0) {
  return job_0.title;
}
function _temp(s) {
  return s.user;
}